import React from "react";
import { FormControlLabel, Grid } from "@mui/material";
import { get } from "lodash";
import { connect } from "react-redux";
import { ConfigPropExtenderHoc } from "../../../../../common/StaticConfigProvider";
import OptionalToolTip from "../../../../../common/toolTip/OptionalToolTip";
import CustomizedSwitch from "../../../../../common/formElements/Switch";
import { useFormikContext } from "formik";
import { Data } from "../../../../../store/terminal/types";

interface ConfigOption {
  label: string;
  field: string;
  tooltip?: string;
}

interface ConfigOptionHoc {
  label: string;
  field: string;
}

interface ConfigurationProps {
  setShowBPGiftCardSupportMenu: (value: boolean) => void;
  setShowFixedAmountEntryMenu: (value: boolean) => void;
  setShowPayAtTableSection: (value: boolean) => void;
  setShowDRCMenu: (value: boolean) => void;
  setShowHTMLReceipts: (value: boolean) => void;
  options: ConfigOption[];
  isUpdateAllowed?: boolean;
  isDisableKeyEntryPassword?: boolean;
  hiddenFields?: string[];
  shouldHideField: (fieldName: string) => boolean;
}

interface ConfigurationHocProps {
  setShowBPGiftCardSupportMenu: (value: boolean) => void;
  options: ConfigOptionHoc[];
  toolTips?: { terminals?: { options?: Record<string, string> } };
  shouldHideField: (fieldName: string) => boolean;
}

/*
 * Most fields are "Yes" / "No" however due to a certain dev (me)
 * some of the terminals use Boolean values true/false for flags
 * we will have to intercept these fields and use true/false for them instead.
 * */
export const getValueForField = (
  field: string,
  checked: boolean
): string | boolean => {
  const fields = [
    "attributes.IdleOnAmountEntry",
    "attributes.TerminalStatusReportingEnabled",
    "attributes.NetworkConnectionManagerEnabled",
    "attributes.TerminalGPSEnabled",
    "attributes.NoCashOutInDebit",
    "attributes.WelfareRestriction",
  ];
  return fields.includes(field) ? checked : checked ? "Yes" : "No";
};

const ConfigurationOptions = ({
  options,
  setShowBPGiftCardSupportMenu,
  setShowFixedAmountEntryMenu,
  setShowPayAtTableSection,
  setShowDRCMenu,
  setShowHTMLReceipts,
  isUpdateAllowed,
  hiddenFields,
  isDisableKeyEntryPassword,
  shouldHideField,
}: ConfigurationProps) => {
  const formikProps = useFormikContext();
  const values = (formikProps.values as unknown) as Data;
  const { setFieldValue } = formikProps;

  const isDisableMotoPassword = () => {
    let checkedValue = false;
    options.map((option) => {
      if (option.field === "attributes.FeatureMOTO") {
        checkedValue =
          values &&
          values.attributes &&
          (Boolean(get(values, option.field)) === true ||
            get(values, option.field) === "Yes");
      }
    });
    return !checkedValue;
  };

  const disableCheck = (field: string) => {
    if (!isUpdateAllowed) return true;
    if (field === "attributes.MOTOPromptPassword")
      return isDisableMotoPassword();
    if (field === "attributes.KeyEntryPromptPassword")
      return isDisableKeyEntryPassword;
  };

  const mutualDisable = (value: boolean, mutualField: string) => {
    if(value) {
      values.attributes[mutualField] === "Yes" ? setFieldValue(`attributes[${mutualField}]`, "No") : ""
    }
  }

  return (
    <Grid container alignItems="flex-start" spacing={2}>
      {options.filter(option=>{
          return !(hiddenFields ?? []).includes(option.field)
      }).map((option) => {
        const checked =
          values &&
          values.attributes &&
          (get(values, option.field) === "Yes" ||
            get(values, option.field) === true);
        return (
          <Grid key={option.field} item xs={12} md={4}>
            <FormControlLabel
              label={
                <OptionalToolTip tooltip={option.tooltip}>
                  {option.label}
                </OptionalToolTip>
              }
              control={
                <CustomizedSwitch
                  name={option.field}
                  checked={checked}
                  onChange={(event, value) => {
                    if (option.field === "attributes.FeatureBPGiftCards") {
                      setShowBPGiftCardSupportMenu(value);
                    }
                    if (option.field === "attributes.PayAtTableEnabled") {
                      setShowPayAtTableSection(value);
                    }
                    if (
                      option.field ===
                      "attributes.FeatureFixedAmountEntryEnabled"
                    ) {
                      setShowFixedAmountEntryMenu(value);
                    }
                    if (
                      option.field === "attributes.FeatureDRCLoyaltyEnabled"
                    ) {
                      setShowDRCMenu(value);
                    }
                    if (option.field === "attributes.EnableHTMLReceipts") {
                      setShowHTMLReceipts(value);
                    }
                    if (option.field === "attributes.USBPOS"){
                      mutualDisable(value, "CloudPOS")
                    }
                    if (option.field === "attributes.CloudPOS"){
                      mutualDisable(value, "USBPOS")
                    }
                    setFieldValue(
                      option.field,
                      getValueForField(option.field, value)
                    );
                  }}
                  disabled={disableCheck(option.field)}
                />
              }
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

const ConnectedConfigurationOptions = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: ConfigurationHocProps) => {
    return {
      ...ownProps,
      shouldHideField: ownProps.shouldHideField,
      options: (ownProps?.options || []).map(({ label, field }) => ({
        label,
        field,
        tooltip: get(
          ownProps,
          `toolTips.terminals.options.configuration.["${label}"]`
        ),
      })),
    };
  }
)(ConfigurationOptions);

const ConnectedConfigurationOptionsHoc = (props: ConfigurationProps) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <ConnectedConfigurationOptions {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedConfigurationOptionsHoc;
